import AppCard from '@nxsys/components/AppCard';
import { defaultTheme } from '@nxsys/constants/defaultConfig';
import { useDashboardEmployerAnalytics } from './context/DashboardEmployerAnalyticsContext';
import { AppState } from '@nxsys/store';
import { Icons } from '@nxsys/types/icons';
import { showCurrencyByCountry } from '@nxsys/utils';
import {
  getCurrentTaxPeriodByFrequency,
  getTaxWeekTaxYear,
} from '@nxsys/utils/taxYear';
import { formatMoney } from '@nxsys/utils/number-helper';
import { IRELAND } from '@nxsys/store/constants/keys';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';

const formatPercent = (value: number) => `${Math.abs(value).toFixed(1)}%`;

const EmployerTaxPeriod = () => {
  const { payrollCountry } = useSelector((state: AppState) => state.appStore);
  const isIreland = payrollCountry === IRELAND;
  const {
    taxPeriodPayments: data,
    loading,
    params,
  } = useDashboardEmployerAnalytics();

  const currency = showCurrencyByCountry(payrollCountry);

  const headerLabel = useMemo(() => {
    const frequency = params?.payFrequencies ?? 'MONTHLY';
    const taxYear = data?.taxYear ?? getTaxWeekTaxYear().taxYear;
    const taxPeriod = getCurrentTaxPeriodByFrequency(frequency);

    if (!taxYear) return '—';

    if (frequency === 'MONTHLY') {
      const safeTaxMonth = Math.min(12, Math.max(1, taxPeriod));

      if (isIreland) {
        // Ireland tax months are 1..12 (Jan..Dec)
        const calendarMonthIndex = safeTaxMonth - 1; // 1->Jan(0), 12->Dec(11)
        const monthLabel = moment({
          year: taxYear,
          month: calendarMonthIndex,
          day: 1,
        }).format('MMM YYYY');
        return `Month ${safeTaxMonth} (${monthLabel})`;
      }

      // UK tax months are 1..12 (Apr..Mar). Convert to calendar month index 0..11.
      const calendarMonthIndex = (safeTaxMonth + 2) % 12; // 1->Apr(3), 10->Jan(0)
      const calendarYear = safeTaxMonth <= 9 ? taxYear - 1 : taxYear;
      const monthLabel = moment({
        year: calendarYear,
        month: calendarMonthIndex,
        day: 1,
      }).format('MMM YYYY');

      return `Month ${safeTaxMonth} (${monthLabel})`;
    }

    return `Week ${taxPeriod}`;
  }, [data?.taxYear, params?.payFrequencies]);

  // NX-5510: the comparison is against the previous period OF THE SELECTED
  // frequency (see backend getStatisticTaxPeriod), so the label must track the
  // frequency instead of always saying "month".
  const comparisonLabel = useMemo(() => {
    switch (params?.payFrequencies ?? 'MONTHLY') {
      case 'WEEKLY':
        return 'vs last week';
      case 'MONTHLY':
        return 'vs last month';
      default:
        return 'vs last period';
    }
  }, [params?.payFrequencies]);

  const items = useMemo(() => {
    const grossCompare = data?.grossPay?.comparePercent ?? 0;
    const netCompare = data?.netPay?.comparePercent ?? 0;

    const ArrowIcon = ({ value }: { value: number }) => (
      <div className={value >= 0 ? '' : 'rotate-180'}>
        <Icons.IconArrowUp
          width={16}
          height={16}
          color={
            value >= 0 ? defaultTheme.theme.palette.primary[500] : '#EF4444'
          }
        />
      </div>
    );

    return [
      {
        title: 'Gross Pay',
        money: formatMoney(data?.grossPay?.total ?? 0, currency),
        description: (
          <div className="flex items-center gap-x-[4px]">
            <ArrowIcon value={grossCompare} />
            <div className="text-[12px] text-gray-500">
              <span className="text-primary-600">
                {formatPercent(grossCompare)}
              </span>{' '}
              {comparisonLabel}
            </div>
          </div>
        ),
        icon: (
          <Icons.IconCoin color={defaultTheme.theme.palette.primary[500]} />
        ),
      },
      {
        title: 'Net Pay',
        money: formatMoney(data?.netPay?.total ?? 0, currency),
        description: (
          <div className="flex items-center gap-x-[4px]">
            <ArrowIcon value={netCompare} />
            <div className="text-[12px] text-gray-500">
              <span className="text-primary-600">
                {formatPercent(netCompare)}
              </span>{' '}
              {comparisonLabel}
            </div>
          </div>
        ),
        icon: (
          <Icons.IconCoin2 color={defaultTheme.theme.palette.primary[500]} />
        ),
      },
      {
        title: isIreland ? 'Revenue Payments' : 'HMRC Payments',
        money: formatMoney(data?.hmrcPayments?.total ?? 0, currency),
        description: data?.hmrcPayments?.daysLeft ? (
          <div className="flex items-center gap-x-[4px] bg-[#FFEDDD] rounded-[4px] py-[4px] px-[8px] w-fit">
            <Icons.IconAlarmClock width={16} height={16} color="#FF8D28" />
            <div className="text-[12px] text-[#FF8D28]">
              {data.hmrcPayments.daysLeft} Days Left
            </div>
          </div>
        ) : undefined,
        icon: (
          <Icons.IconInvoice color={defaultTheme.theme.palette.primary[500]} />
        ),
      },
      ...(!isIreland
        ? [
            {
              title: 'Attachment Orders',
              money: formatMoney(data?.attachmentOrders?.total ?? 0, currency),
              icon: (
                <Icons.IconPaperText
                  color={defaultTheme.theme.palette.primary[500]}
                />
              ),
            },
          ]
        : []),
      {
        title: 'Total Pension Payments',
        money: formatMoney(data?.totalPensionPayments?.total ?? 0, currency),
        icon: (
          <Icons.IconFileUser color={defaultTheme.theme.palette.primary[500]} />
        ),
      },
    ];
  }, [currency, data, comparisonLabel]);

  return (
    <div className="EmployerTaxPeriod">
      <AppCard
        title={
          <div className="flex items-center justify-between">
            <div className="text-[22px]">Tax Period</div>
            <div className="flex items-center gap-x-[8px]">
              <Icons.IconClock />
              <div className="text-[14px]">
                {loading ? 'Loading…' : headerLabel}
              </div>
            </div>
          </div>
        }
      >
        <div className="grid grid-cols-3 gap-[16px] max-sm:grid-cols-1 p-[16px]">
          {items.map((item) => (
            <div
              key={item.title}
              className="border border-[#CDDDD9] dashboard-hover-card rounded-[8px] p-[16px]"
            >
              <div className="bg-[#E6F6F2] rounded-[8px] p-[8px] mb-[16px] w-fit">
                {item.icon}
              </div>
              <div className="text-[12px] mb-[4px]">{item.title}</div>
              <div className="text-[28px] font-bold mb-[4px]">{item.money}</div>
              {item.description}
            </div>
          ))}
        </div>
      </AppCard>
    </div>
  );
};

export default EmployerTaxPeriod;
